import { Spinner } from '@app/components/icons/spinner';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { useTranslation } from '@hopin-team/provider-translation';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, resolvePath, useParams } from 'react-router-dom';

import { ContextMenu as ContextMenuV2 } from '../../../../components/context-menu-v2';
import { useRightPanelGroups } from '../../../../hooks/use-right-panel-groups';
import {
  selectActiveAdvancedEdit,
  selectAdvancedEditsState,
  selectIsEditFrozen,
  selectRecording,
  selectRecordingFetchStatus,
} from '../../../../selector';
import { resolveDraftCutsWithFallback } from '../../../../utils';
import { EditorHeader } from '../editor-header';
import { AdvancedTimelineView } from './advanced-timeline-view';
import { EditAspectRatioDropdown } from './edit-aspect-ratio-dropdown';
import {
  EditViewPlayerProvider,
  useEditViewPlayer,
} from './edit-view-player-context';
import {
  type SegmentVideoPlayerRecording,
  SegmentVideoPlayer,
} from './segment-video-player';
import { splitCutAtTime } from './utils/cut-operations';
import {
  computeJointsFromSegments,
  cutsToSegments,
  mapOutputToSourceMs,
} from './utils/cuts-to-segments';

const EditViewMainContent = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const recording = useAppSelector(selectRecording);
  const activeEdit = useAppSelector(selectActiveAdvancedEdit);
  const isFrozen = useAppSelector(selectIsEditFrozen);
  const {
    playerRef,
    onPlaybackStateChange,
    outputTime,
    seek,
    zoomScale,
    setZoomScale,
  } = useEditViewPlayer();
  const rightPanelGroups = useRightPanelGroups({ preset: 'edit' });

  const [selectedSegmentId, setSelectedSegmentId] = useState<string | null>(
    null,
  );
  const [selectedJointId, setSelectedJointId] = useState<string | null>(null);

  const canEdit = Boolean(activeEdit) && !isFrozen;

  const recordingDurationSec = recording?.duration ?? 0;
  const cuts = resolveDraftCutsWithFallback({
    draftCuts: activeEdit?.draft?.cuts ?? [],
    recordingId: recording?.id,
    recordingDurationSec,
  });
  const segments = cutsToSegments(
    cuts,
    recording?.id ?? 'recording',
    recordingDurationSec,
  );
  const activeSegments = segments.filter(segment => !segment.isDeleted);
  const outputDurationSec = activeSegments.at(-1)?.outputEndTime ?? 0;
  const outputDuration = outputDurationSec / 1000;

  // Cannot delete the only remaining segment — there must always be at least one.
  const canDeleteSelectedSegment =
    canEdit && Boolean(selectedSegmentId) && activeSegments.length > 1;

  // Disable split when a joint is selected — the user's intent is to act on
  // the cut boundary (Del to restore it), not to add another split.
  const canSplitAtPlayhead =
    canEdit &&
    outputTime > 0 &&
    outputTime < outputDuration &&
    !selectedJointId;

  // Determine whether the selected joint has deleted content between its
  // neighbours so the hint can say "restore segment" vs "remove cut".
  const joints = useMemo(
    () => computeJointsFromSegments(segments, recording?.id ?? 'recording'),
    [segments, recording?.id],
  );
  const selectedJoint = selectedJointId
    ? joints.find(j => j.id === selectedJointId)
    : null;
  const selectedJointHasDeletedContent =
    (selectedJoint?.deletedDuration ?? 0) > 0;

  const hints = useMemo<string[]>(() => {
    if (!canEdit) return [];
    // Joint selected: single contextual action, no split allowed.
    if (selectedJointId) {
      return [
        selectedJointHasDeletedContent
          ? t('content-recording.editor-v2.hint-joint-deleted-segment')
          : t('content-recording.editor-v2.hint-joint-selected'),
      ];
    }
    const result: string[] = [];
    // Show the split hint whenever a split is actually possible.
    if (canSplitAtPlayhead)
      result.push(t('content-recording.editor-v2.hint-default'));
    // Show the delete hint only when there is more than one segment to delete.
    if (canDeleteSelectedSegment)
      result.push(t('content-recording.editor-v2.hint-segment-selected'));
    return result;
  }, [
    canDeleteSelectedSegment,
    canEdit,
    canSplitAtPlayhead,
    selectedJointHasDeletedContent,
    selectedJointId,
    t,
  ]);

  const handleSplitAtPlayhead = useCallback(() => {
    if (!activeEdit || !canSplitAtPlayhead) {
      return;
    }
    const sourceTimeMs = mapOutputToSourceMs(segments, outputTime);
    if (sourceTimeMs == null) {
      return;
    }
    dispatch({
      type: '[advanced-editor] edit draft changed',
      payload: {
        editId: activeEdit.id,
        draft: {
          ...activeEdit.draft,
          cuts: splitCutAtTime(cuts, sourceTimeMs),
        },
        previousDraft: activeEdit.draft,
      },
    });
    seek(outputTime);
  }, [
    activeEdit,
    canSplitAtPlayhead,
    cuts,
    dispatch,
    outputTime,
    seek,
    segments,
  ]);

  const recordings: SegmentVideoPlayerRecording[] =
    recording?.id && recording?.playable_url
      ? [{ id: recording.id, hlsUrl: recording.playable_url }]
      : [];

  return (
    <>
      {/* ── Middle row ── */}
      <div className="flex flex-row flex-1 min-h-0">
        {/* Center — layout matches V1 editor-page (dropdown above player) */}
        <div className="flex-1 min-h-0 min-w-0 pt-6 pb-0 px-4 flex flex-col gap-7 justify-center items-center overflow-hidden">
          {recordings.length > 0 ? (
            <>
              <EditAspectRatioDropdown />
              <SegmentVideoPlayer
                ref={playerRef}
                recordings={recordings}
                segments={segments}
                outputDuration={outputDuration}
                onPlaybackStateChange={onPlaybackStateChange}
                zoomScale={zoomScale}
                onZoomScaleChange={setZoomScale}
                onSplitAtPlayhead={handleSplitAtPlayhead}
                canSplitAtPlayhead={canSplitAtPlayhead}
                hints={hints}
                className="h-full w-full min-h-0"
              />
            </>
          ) : (
            <div className="text-xs text-spring-neutral-b0/60">
              {t('content-recording.editor-v2.segment-player-empty')}
            </div>
          )}
        </div>

        {/* Right Panel — reused ContextMenuV2 (direct flex child, no wrapper needed) */}
        <ContextMenuV2
          groups={rightPanelGroups}
          className="shrink-0"
          isCollapsible
        />
      </div>

      {/* ── Footer (full track container) ── */}
      <div className="h-28 shrink-0 border-t border-spring-neutral-b0/15">
        <AdvancedTimelineView
          onSplitAtPlayhead={handleSplitAtPlayhead}
          selectedSegmentId={selectedSegmentId}
          selectedJointId={selectedJointId}
          onSelectSegment={setSelectedSegmentId}
          onSelectJoint={setSelectedJointId}
        />
      </div>
    </>
  );
};

export const EditView = () => {
  const { editId } = useParams<{ editId: string }>();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const recordingFetchStatus = useAppSelector(selectRecordingFetchStatus);
  const advancedEditsState = useAppSelector(selectAdvancedEditsState);
  const isCreatingEdit = advancedEditsState.status === 'processing';

  // Read editFetchStatus directly from editFetchStatusById[editId] rather than
  // going through activeEditId. This is important because RecordingEffect
  // re-runs on every route change (including sub-route navigation from
  // /editor → /editor/edits/:editId) and calls initializeRecording, which
  // dispatches [recording] fetch recording started. That action resets the
  // entire advancedEdits slice via createDefaultAdvancedEditsState(), nulling
  // both activeEditId and editFetchStatusById. If we read through activeEditId
  // (as selectActiveEditFetchStatus does), the selector returns 'idle' forever
  // once activeEditId is null, causing a permanent spinner.
  const editFetchStatus = useAppSelector(
    state =>
      (editId &&
        (state.contentHub.recordingInfo.advancedEdits.editFetchStatusById[
          editId
        ] ??
          'idle')) ||
      'idle',
  );

  // Same reasoning: read activeEditId directly so we can detect when it has
  // been reset and re-dispatch [advanced-editor] edit selected.
  const activeEditId = useAppSelector(
    state => state.contentHub.recordingInfo.advancedEdits.activeEditId,
  );

  // Block rendering until both the recording AND the full edit entity are
  // loaded. This ensures:
  //   1. The player never renders with null recording data.
  //   2. The editor never renders with a placeholder entity (revision: 0,
  //      default captions, missing backgroundColor).
  //   3. The auto-save saga always has a valid revision on first PUT.
  const isLoading =
    recordingFetchStatus === 'idle' ||
    recordingFetchStatus === 'processing' ||
    editFetchStatus === 'idle' ||
    editFetchStatus === 'loading';

  const hasFailed =
    recordingFetchStatus === 'failed' || editFetchStatus === 'failed';

  // Effect 1a: keep activeEditId in sync with the URL param.
  // Runs on mount AND whenever activeEditId is reset (e.g. by a
  // RecordingEffect restart resetting advancedEdits to default state).
  // The activeEditId === editId guard prevents an infinite dispatch loop.
  useEffect(() => {
    if (!editId || activeEditId === editId) return;
    dispatch({ type: '[advanced-editor] edit selected', payload: { editId } });
  }, [editId, dispatch, activeEditId]);

  // Effect 1b: cleanup only — dispatch edit deselected on route unmount.
  // Separated from Effect 1a so the cleanup does not re-run on every
  // activeEditId change (which would cause a deselect → reselect thrash).
  useEffect(() => {
    return () => {
      dispatch({ type: '[advanced-editor] edit deselected' });
    };
  }, [dispatch]);

  // Mirror <Layout> behaviour — reset fetch status and redirect to library on error
  useEffect(() => {
    if (recordingFetchStatus === 'failed') {
      dispatch({ type: '[recording] fetch recording reset' });
    }
  }, [recordingFetchStatus, dispatch]);

  if (hasFailed) {
    const libraryPath = resolvePath(
      '../../../library',
      window.location.pathname,
    );
    return <Navigate to={libraryPath.pathname} replace />;
  }

  if (isLoading) {
    return <Spinner size="large" className="mt-24" theme="dark" />;
  }

  return (
    <div className="relative flex h-full flex-col">
      <EditorHeader mode="edit" />
      <EditViewPlayerProvider>
        <EditViewMainContent />
      </EditViewPlayerProvider>

      {isCreatingEdit && (
        <div
          className="absolute inset-0 z-50 flex items-center justify-center bg-black/45"
          role="status"
          aria-live="polite"
          aria-label={t('content-recording.editor-v2.clone-edit-loading')}
        >
          <Spinner size="large" theme="dark" />
        </div>
      )}
    </div>
  );
};
